/**
 * MCP가 읽는 앱 상태 스냅샷. 저장 형식(PersistedV3)과 편집기의 확정된 값에서 만든다.
 *
 * - 편집 중인 프로젝트는 편집기 사본이 카탈로그보다 최대 250ms 앞서 있으므로 편집기 값을 쓴다.
 * - 그리드 셀에 입력 중인(확정 전) 값은 React 지역 상태라 여기 들어오지 않는다. **확정된 값만** 돌려준다(ADV-I08).
 * - 스냅샷은 읽기만 한다. 도구는 스토어를 바꾸지 않는다(ADV-I07).
 */
import type { OrganizationTeam, Project, UserResource } from "@/engine/types";
import type { ReportPeriod } from "@/features/reports/report-model";
import type { PersistedV3 } from "@/store/persist";

export interface McpWorkspace {
  id: string;
  name: string;
  /** 최근 수정 순(앱 목록과 같은 순서) */
  projects: Project[];
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
  /** 프로젝트별 마감한 보고 기간(최신 먼저) */
  reports: Record<string, ReportPeriod[]>;
}

export interface McpSnapshot {
  activeWorkspaceId: string;
  workspaces: McpWorkspace[];
}

export interface EditorOverlay {
  activeId: string | null;
  project: Project;
  reports: ReportPeriod[];
}

const byUpdatedDesc = (a: Project, b: Project) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0);

export function snapshotFromCatalog(data: PersistedV3, editor?: EditorOverlay): McpSnapshot {
  const workspaces: McpWorkspace[] = Object.values(data.workspaces).map((ws) => {
    const projects: Record<string, Project> = { ...ws.projects };
    const reports: Record<string, ReportPeriod[]> = {};
    for (const [id, chrome] of Object.entries(ws.byProject ?? {})) reports[id] = chrome?.reports ?? [];
    if (editor?.activeId && projects[editor.activeId]) {
      projects[editor.activeId] = editor.project;
      reports[editor.activeId] = editor.reports;
    }
    return {
      id: ws.id,
      name: ws.name,
      projects: Object.values(projects).sort(byUpdatedDesc),
      resources: ws.resources ?? {},
      teams: ws.teams ?? {},
      reports,
    };
  });
  return { activeWorkspaceId: data.activeWorkspaceId, workspaces };
}

export function findProject(snap: McpSnapshot, projectId: string): { ws: McpWorkspace; project: Project } | null {
  for (const ws of snap.workspaces) {
    const project = ws.projects.find((p) => p.id === projectId);
    if (project) return { ws, project };
  }
  return null;
}
